import { useState, useEffect } from 'react';
import _ from 'lodash';
import SAlert from 'react-s-alert';
import { isVideoOrPlayback } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import { compressJson } from '@scale/shared/compression';
import { fetchApi } from 'react-scale/redux/actions';
import useInterval from 'react-scale/hooks/useInterval';
import logger from '@scale/frontend-lib/logging';
import { useAsyncFn, useThrottle } from 'react-use-v13';
import useUnleashFeatureFlag from 'react-scale/components/featureFlag/useUnleashFeatureFlag';
import { Claim } from '@scale/shared/types/queue';
import { PerAnnotationError } from '@scale/shared/types/customerAudit';
import redirect from '@scale/frontend-lib/isomorphic-redirect';
import useShortcut from 'react-scale/hooks/useShortcut';
import { ShortcutType, DefaultTriggers } from '@scale/shared/types/preference';

const FLUSH_INTERVAL = 5 * 60 * 1000;
const AUTOSAVE_403_ERROR_COUNT_THRESHOLD = 3;

const checkIfDataIsEmpty = (data: any, taskType: TaskType) => {
  // TODO: add this functionality for other task types
  if (isVideoOrPlayback(taskType)) {
    return _.every(Object.values(data), (elem?: any) => {
      return !elem || !_.isArray(elem) || elem.length === 0;
    });
  }

  return false;
};

enum AutosaveTypes {
  Load = 'Load',
  Save = 'Save',
  Delete = 'Delete',
}
interface AutosaveOpts<T> {
  data: T;
  id: string; // Subtask ID or training attempt ID or task id (audit)
  worker: string;
  attemptId?: string; // task attempt id, if applicable.
  taskType: TaskType;
  projectId?: string;
  isCustomer?: boolean; // audit means it is form customer
  keyInfo?: Record<string, string>; // additional info to key the autosave on.
  claim?: Claim;
  perAnnotationErrors?: PerAnnotationError[]; // For audits
  customFlushInterval?: number;
}

export interface AutosaveHook<T> {
  start: () => void;
  stop: () => void;
  load: () => Promise<T>;
  flush: () => Promise<void>;
  discard: () => Promise<void>;
  lastAutosaveTime: Date;
  isAutosaving: boolean;
}

export const flushCheckpoint = async ({
  assignmentId, // Subtask ID or training attempt ID
  taskType,
  body,
  projectId,
  hasClaim,
  onAutosaveError,
  onSuccessfulAutosave,
  onNoClaim,
  noData,
}: {
  assignmentId: string;
  taskType: TaskType;
  body: $FIXME;
  projectId?: string;
  hasClaim?: boolean;
  onAutosaveError?: (error: any) => void;
  onSuccessfulAutosave?: () => void;
  onNoClaim?: (autosaveType: AutosaveTypes) => void;
  noData?: boolean;
}) => {
  if (!hasClaim) {
    return;
  }

  if (noData) {
    SAlert.warning('Unable to save because no changes have been made');
    return;
  }

  try {
    const resp = await fetchApi(`/${taskType}/${assignmentId}/checkpoint`, {
      body: { content: compressJson(body) },
      method: 'POST',
      maxRetries: 3,
      parseAndHandle: true,
      redirectOnUnauthorized: true,
    });
    if (!resp.hasClaim && !resp.saved) {
      onNoClaim && onNoClaim(AutosaveTypes.Save);
    } else if (!resp.saved) {
      throw resp;
    }

    onSuccessfulAutosave && onSuccessfulAutosave();
    return resp;
  } catch (error) {
    onAutosaveError && onAutosaveError({ autosaveError: error });
    logger.error(`Saving failed for task, response is ${JSON.stringify(error)}`, {
      taskType,
      assignmentId,
      projectId,
      worker: body.worker,
    });
    SAlert.error('Task saving failed. If this persists, please contact our support team.', {
      timeout: 'none',
    });
    SAlert.info('Enabling local saving');
  }
};

const discardCheckpoint = async ({
  assignmentId, // Subtask ID or training attempt ID
  taskType,
  body,
  projectId,
  hasClaim,
  onAutosaveError,
  onSuccessfulAutosave,
  onNoClaim,
}: {
  assignmentId: string;
  taskType: TaskType;
  body: $FIXME;
  projectId?: string;
  hasClaim?: boolean;
  onAutosaveError?: (error: any) => void;
  onSuccessfulAutosave?: () => void;
  onNoClaim?: (autosaveType: AutosaveTypes) => void;
}) => {
  if (!hasClaim) {
    return;
  }

  logger.info(`Discarding save for task`, { taskType, assignmentId, projectId });

  try {
    const resp = await fetchApi(`/${taskType}/${assignmentId}/checkpoint`, {
      body,
      method: 'DELETE',
      maxRetries: 3,
      parseAndHandle: true,
      redirectOnUnauthorized: true,
    });

    if (!resp.hasClaim && !resp.deleted) {
      onNoClaim && onNoClaim(AutosaveTypes.Delete);
    } else if (!resp.deleted) {
      throw resp;
    } else {
      SAlert.success('Your save has been discarded successfully.', { timeout: 'none' });
    }

    onSuccessfulAutosave && onSuccessfulAutosave();
    return resp;
  } catch (error) {
    onAutosaveError && onAutosaveError(error);
    logger.error(`Save deletion failed for task, response is ${JSON.stringify(error)}`, {
      taskType,
      assignmentId,
      projectId,
      worker: body.worker,
    });
    SAlert.error('Save deletion failed. If this persists, please contact our support team.', {
      timeout: 'none',
    });
  }
};

const loadCheckpoint = async ({
  assignmentId,
  taskType,
  projectId,
  attemptId,
  isCustomer,
  keyInfo = {},
  onAutosaveError,
  onSuccessfulAutosave,
  setHasClaim,
}: {
  assignmentId: string;
  taskType: TaskType;
  projectId?: string;
  attemptId?: string;
  isCustomer: boolean;
  keyInfo?: Record<string, string>;
  onAutosaveError?: (error: any) => void;
  onSuccessfulAutosave?: () => void;
  setHasClaim?: (claim: boolean) => void;
}): Promise<any> => {
  if (process.env.STORYBOOK_OFFLINE_MODE) {
    return {};
  }

  // We reset claim to true whenever we load the save since its possible/likely
  // that we are loading a new task. Shouldn't affect taskers since they always
  // have claim on tasks they do
  setHasClaim && setHasClaim(true);

  try {
    const response = await fetchApi(`/${taskType}/${assignmentId}/checkpoint`, {
      query: { attemptId, isCustomer: isCustomer ? 1 : 0, ...keyInfo },
      method: 'GET',
      maxRetries: 3,
      parseAndHandle: true,
      redirectOnUnauthorized: true,
    });
    const { success, ...data } = response;

    if (!success || checkIfDataIsEmpty(data, taskType)) {
      return null;
    }

    if (success && _.isEmpty(data)) {
      logger.warn(`Loaded save is empty for task, response is ${JSON.stringify(response)}`, {
        taskType,
        assignmentId,
        projectId,
      });
    }

    onSuccessfulAutosave && onSuccessfulAutosave();
    return data;
  } catch (error) {
    onAutosaveError && onAutosaveError(error);
    logger.error(`Unable to load save for task, response is ${JSON.stringify(error)}`, {
      taskType,
      assignmentId,
      projectId,
    });
    SAlert.error('Save loading failed. If this persists, please contact our support team.', {
      timeout: 'none',
    });
    SAlert.info('Enabling local saving');
    return null;
  }
};

const localBackupKey = 'useLocalAutosave';

const setLocalAutosave = (id: string, data: unknown) => {
  if (!process.browser) {
    return;
  }
  try {
    const item = JSON.stringify({
      id,
      data,
      time: Date.now(),
    });
    window.localStorage.setItem(localBackupKey, item);
  } catch (e) {
    logger.warn(e.message);
  }
};

const getLocalAutosave = (id: string) => {
  if (!process.browser) {
    return;
  }

  try {
    const itemString = window.localStorage.getItem(localBackupKey);
    if (!itemString) return;
    const item = JSON.parse(itemString);
    // Only for browser refresh.
    if (item.id !== id) {
      return;
    }
    return item;
  } catch (e) {
    logger.warn(e.message);
    return;
  }
};

const deleteLocalAutosave = () => {
  if (!process.browser) {
    return;
  }
  try {
    window.localStorage.removeItem(localBackupKey);
  } catch (e) {
    return;
  }
};

function useAutosave<T>({
  id,
  attemptId,
  worker,
  data,
  taskType,
  projectId,
  isCustomer = false,
  keyInfo = {},
  claim,
  customFlushInterval,
}: AutosaveOpts<T>): AutosaveHook<T> {
  const [lastAutosaveTime, setLastAutosaveTime] = useState(new Date());
  const [hasClaim, setHasClaim] = useState(true);
  const [autosave403InARowCount, setAutosave403InARowCount] = useState<number>(0);

  const onSuccessfulAutosave = () => {
    setAutosave403InARowCount(0);
  };

  const onAutosaveError = ({ autosaveError }: { autosaveError: any }) => {
    if (autosaveError?.status === 403) {
      setAutosave403InARowCount(autosave403InARowCount + 1);
    }
  };

  const onNoClaim = (autosaveType: AutosaveTypes) => {
    SAlert.warning(
      `WARNING: ${autosaveType} operation failed because you do not have claim on this task.`,
      {
        timeout: 'none',
        offset: 25,
      },
    );
    setHasClaim(false);
  };

  useEffect(() => {
    if (autosave403InARowCount >= AUTOSAVE_403_ERROR_COUNT_THRESHOLD) {
      logger.error(
        `Saving repeatedly failed with error code 403 more than the accepted max threshold`,
        {
          autosave403InARowCount,
          autosave403ErrorCountThreshold: AUTOSAVE_403_ERROR_COUNT_THRESHOLD,
          assignmentId: id,
          projectId,
          taskType,
        },
      );

      window.alert('Something went wrong which requires a page refresh :(');
      redirect(null, '/login', {
        includeCurrentURL: true,
        clear: true,
      });
    }
  }, [autosave403InARowCount]);

  // Handle race condition with local backups.
  let localAutosaveId =
    (useUnleashFeatureFlag('local-autosave', { projectId }, false) ||
      !!localStorage.localAutosave) &&
    claim?.claimedUntil &&
    claim.claimedUntil > new Date()
      ? ''
      : undefined;
  if (localAutosaveId === '' && claim?.claimedUntil && claim.claimedUntil > new Date())
    localAutosaveId = id;

  const noData = checkIfDataIsEmpty(data, taskType);

  const [submitFlush, flush] = useAsyncFn(async () => {
    const resp = await flushCheckpoint({
      assignmentId: id,
      taskType,
      body: {
        worker,
        attemptId,
        ...data,
        isCustomer: isCustomer ? 1 : 0,
        keyInfo,
      },
      projectId,
      hasClaim,
      onAutosaveError,
      onSuccessfulAutosave,
      onNoClaim,
      noData,
    });

    if (resp && resp.saved && resp.hasClaim) {
      setLastAutosaveTime(new Date());
      // If autosave fails to save, activate local autosave.
    } else if (claim?.claimedUntil && claim.claimedUntil > new Date()) {
      localAutosaveId = id;
    }
  }, [id, taskType, attemptId, worker, data, projectId]);

  const [submitDiscard, discard] = useAsyncFn(async () => {
    const resp = await discardCheckpoint({
      assignmentId: id,
      taskType,
      body: { worker, attemptId, ...data, isCustomer: isCustomer ? 1 : 0, keyInfo },
      projectId,
      hasClaim,
      onAutosaveError,
      onSuccessfulAutosave,
      onNoClaim,
    });

    if (resp && resp.deleted && resp.hasClaim) {
      setLastAutosaveTime(new Date());
    }

    if (localAutosaveId === id) {
      deleteLocalAutosave();
    }
  }, [id, taskType, attemptId, worker, data, projectId]);

  const [isLoading, load] = useAsyncFn(async () => {
    const checkpoint = await loadCheckpoint({
      assignmentId: id,
      taskType,
      projectId,
      attemptId,
      isCustomer,
      keyInfo,
      onAutosaveError,
      onSuccessfulAutosave,
      setHasClaim,
    });
    // If loading fails activate local autosave.
    if (!checkpoint && claim?.claimedUntil && claim.claimedUntil > new Date()) localAutosaveId = id;
    const localBackup = getLocalAutosave(id + taskType);
    if (!_.isEmpty(localBackup) && (localBackup.time > checkpoint.date || _.isEmpty(checkpoint))) {
      logger.info(`Restored backup from local storage after refreshing the page`, {
        assignmentId: id,
        projectId,
        taskType,
      });
      return localBackup.data;
    }
    return checkpoint;
  }, [id, taskType, projectId, attemptId, keyInfo, isCustomer]);

  const [start, stop] = useInterval(flush, customFlushInterval ?? FLUSH_INTERVAL);

  // Save a local storage backup every 10s in case the page breaks.
  useThrottle(() => {
    if (localAutosaveId === id) {
      setLocalAutosave(id + taskType, data);
      setLastAutosaveTime(new Date());
    }
  }, 10 * 1000);

  const isAutosaving = submitFlush.loading || submitDiscard.loading || isLoading.loading;

  const flushWrapper = async () => {
    if (isAutosaving) {
      // HACK: Chat task autosaves on every step submission, so this warning is too noisy.
      if (taskType === TaskType.Chat || taskType === TaskType.MultiChat) {
        return;
      }
      SAlert.warning('Cannot save while another save action is in progress');
    } else if (!hasClaim) {
      SAlert.warning('WARNING: You may not save because you do not have claim on this task');
    } else {
      await flush();
    }
  };

  const discardWrapper = async () => {
    if (isAutosaving) {
      SAlert.warning('Cannot discard save while another save action is in progress');
    } else if (!hasClaim) {
      SAlert.warning(
        'WARNING: You may not delete your save because you do not have claim on this task',
      );
    } else {
      await discard();
    }
  };

  useShortcut({
    trigger: DefaultTriggers[ShortcutType.Autosave],
    description: `Save annotations`,
    action: flushWrapper,
    disabled: false,
    type: ShortcutType.Autosave,
    preventDefault: true,
  });

  return {
    start,
    stop,
    load,
    flush: flushWrapper,
    discard: discardWrapper,
    lastAutosaveTime,
    isAutosaving,
  } as const;
}

export function useAutosaveMock(): AutosaveHook<null> {
  return {
    start: () => {},
    stop: () => {},
    load: async () => {
      return null;
    },
    flush: async () => {},
    discard: async () => {},
    lastAutosaveTime: new Date(),
    isAutosaving: false,
  } as const;
}

export default useAutosave;
